import { useQuery } from '@tanstack/react-query';
import { api } from '~/lib/api-client';

export interface BranchInfo {
  branchId: string;
  /** LLM-generated short title for the branch; null until the title call lands. */
  title?: string | null;
  firstUserPreview: string;
  messageCount: number;
  lastActivityAt: string;
  branchedFromMessageId: number | null;
  branchedFromBranchId: string | null;
}

interface BranchesResponse {
  branches: BranchInfo[];
  activeBranchId: string;
}

export function useBranches(sessionId: string) {
  return useQuery({
    queryKey: ['chat', 'branches', sessionId],
    queryFn: async () => {
      const data = await api.get<BranchesResponse>(`/api/v2/chat/${sessionId}/branches`);
      // Keep the server's resolved active branch alongside the list — it's the
      // single source of truth for "which branch should be on screen" after a
      // reload. Discarding it (the old behaviour) left the client guessing
      // "main" while the server returned a fork's history (FRONT-258).
      return {
        branches: data.branches ?? [],
        activeBranchId: data.activeBranchId ?? 'main',
      };
    },
    staleTime: 5_000,
  });
}

/**
 * Decide which branch should be on screen once the server reports its resolved
 * active branch (the most-recently-active branch in the session). The server's
 * value is the single source of truth on load, but an explicit user choice
 * (branch switch / retry) always wins so we never yank the user off the branch
 * they just selected. Returns `current` unchanged when there's nothing to
 * adopt — the caller can pass it straight to a setState updater and React will
 * bail out of the re-render. (FRONT-258)
 */
export function nextActiveBranch(
  current: string,
  serverResolved: string | undefined | null,
  userPicked: boolean,
): string {
  if (userPicked) return current;
  if (!serverResolved) return current;
  return serverResolved;
}

/**
 * Build a lookup from "fork-anchor message id" → ordered branches that share
 * that fork point. The first element is the parent branch (where the original
 * user message lives); the rest are the retried clones. Used by the message
 * bubble to render a `‹ k / N ›` sibling-branch navigator.
 */
export function buildForkGroups(branches: BranchInfo[]): Map<number, BranchInfo[]> {
  const groups = new Map<number, BranchInfo[]>();
  for (const b of branches) {
    if (b.branchedFromMessageId == null) continue;
    const arr = groups.get(b.branchedFromMessageId) ?? [];
    arr.push(b);
    groups.set(b.branchedFromMessageId, arr);
  }
  const byId = new Map(branches.map((b) => [b.branchId, b]));
  for (const [, arr] of groups.entries()) {
    const parentId = arr[0]?.branchedFromBranchId;
    if (!parentId) continue;
    const parent = byId.get(parentId);
    if (parent && !arr.some((x) => x.branchId === parent.branchId)) {
      arr.unshift(parent);
    }
  }
  return groups;
}
